<template>
  <div class="request-monitor">
    <header class="rm-header">
      <h1>Request Monitor</h1>
      <div class="toolbar">
        <div class="input-group">
          <input
            v-model="search"
            type="search"
            class="input"
            placeholder="Search user, tx hash, to address…"
            @keyup.enter="applySearch"
            aria-label="Search requests"
          />
          <button class="btn btn--ghost" @click="applySearch" title="Search">Search</button>
        </div>

      <select v-model="statusFilter" class="select" aria-label="Filter by status">
  <option value="all">All statuses</option>
  <option value="pending">Pending</option>
  <option value="queued">Queued</option>
  <option value="complete">Complete</option>
  <option value="error">Error</option>
</select>


        <button class="btn" @click="refresh" :disabled="isLoading">
          {{ isLoading ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </header>

    <p v-if="statusMessage" :class="['status', statusType]">{{ statusMessage }}</p>

    <!-- Results group (table + cards + pagination) -->
    <template v-if="filteredRequests.length">
      <!-- Desktop / Tablet table -->
      <div class="table-wrap">
        <table class="rm-table">
          <thead>
            <tr>
              <th>Req ID</th>
              <th>User</th>
              <th>Tx Hash</th>
              <th>To</th>
              <th>Type</th>
              <th>Mode</th>
              <th class="col-hop">Hop</th>
              <th>Status</th>
              <th>Requested At</th>
              <th>Bug?</th>
              <th>Result</th>
              <th>Screenshot</th>
              <th>Full Result</th>
              <th>Actions</th>

            </tr>
          </thead>
          <tbody>
            <tr
              v-for="req in pagedRequests"
              :key="req.request_id"
              :class="rowClass(req)"
            >
              <td class="mono">{{ req.request_id }}</td>
              <td class="semi">{{ req.username }}</td>

              <td class="mono truncate">
                <span :title="req.transaction_hash">{{ req.transaction_hash }}</span>
                <button class="icon-btn" @click="copy(req.transaction_hash)" title="Copy tx hash">📋</button>
              </td>

              <td class="mono truncate">
                <span :title="req.to_address || '—'">{{ req.to_address || '—' }}</span>
                <button
                  v-if="req.to_address"
                  class="icon-btn"
                  @click="copy(req.to_address)"
                  title="Copy address"
                >📋</button>
              </td>

              <td class="semi">{{ req.request_type }}</td>
              <td class="semi">{{ req.mode }}</td>
<td class="semi col-hop">{{ formatHopLogic(req.hop_logic) }}</td>
              <td>
                <span :class="['pill', pillClass(req)]">
                  {{ req.status }}
                </span>
                <span
                  v-if="isStale(req.request_time) && isQueueLike(req)"

                  class="pill pill--warn"
                  title="Stale"
                >stale</span>
              </td>

              <td class="semi">{{ formatTime(req.request_time) }}</td>
              <td class="semi">{{ req.bug_reported === 'yes' ? '✅' : '❌' }}</td>

              <td class="result-cell">
                <details>
                  <summary>View</summary>
                  <pre>{{ req.result || '—' }}</pre>
                </details>
              </td>

              <td>
                <button @click="viewScreenshot(req.request_id)" class="btn btn--ghost small">View Screenshot</button>
              </td>

              <td class="result-cell">
                <details v-if="req.full_result">
                  <summary>
                    Full
                    <button
                      class="icon-btn"
                      @click.stop="copy(req.full_result)"
                      title="Copy full result"
                    >📋</button>
                  </summary>
                  <pre class="scroll">{{ req.full_result }}</pre>
                </details>
                <span v-else>—</span>
              </td>
              <td>
  <button
    class="btn small"
    :disabled="isRequeuing(req.request_id) || normalizeStatus(req.status)==='queued'"
    @click="confirmRequeue(req)"
    title="Set status back to queued"
  >
    {{ isRequeuing(req.request_id) ? 'Requeuing…' : 'Requeue' }}
  </button>
  
</td>

            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards (hidden on desktop via CSS) -->
      <div class="cards">
        <article
          v-for="req in pagedRequests"
          :key="'m-'+req.request_id"
          :class="['card', rowClass(req)]"
        >
          <header class="card__header">
            <div class="card__title mono">#{{ req.request_id }}</div>
            <div class="card__tags">
              <span :class="['pill', pillClass(req)]">{{ req.status }}</span>
              <span
            v-if="isStale(req.request_time) && isQueueLike(req)"

                class="pill pill--warn"
              >stale</span>
            </div>
          </header>

          <dl class="kv">
            <div><dt>User</dt><dd>{{ req.username }}</dd></div>
            <div><dt>Tx Hash</dt>
              <dd class="mono truncate">
                <span :title="req.transaction_hash">{{ req.transaction_hash }}</span>
                <button class="icon-btn" @click="copy(req.transaction_hash)" title="Copy tx hash">📋</button>
              </dd>
            </div>
            <div><dt>To</dt>
              <dd class="mono truncate">
                <span :title="req.to_address || '—'">{{ req.to_address || '—' }}</span>
                <button v-if="req.to_address" class="icon-btn" @click="copy(req.to_address)" title="Copy address">📋</button>
              </dd>
            </div>
            <div><dt>Type</dt><dd>{{ req.request_type }}</dd></div>
            <div><dt>Mode</dt><dd>{{ req.mode }}</dd></div><div><dt>Hop</dt><dd>{{ formatHopLogic(req.hop_logic) }}</dd></div>

            <div><dt>Requested</dt><dd>{{ formatTime(req.request_time) }}</dd></div>
            <div><dt>Bug?</dt><dd>{{ req.bug_reported === 'yes' ? '✅' : '❌' }}</dd></div>
          </dl>

          <div class="card__actions">
            <details>
              <summary>Result</summary>
              <pre class="scroll">{{ req.result || '—' }}</pre>
            </details>

            <details v-if="req.full_result">
              <summary>
                Full Result
                <button class="icon-btn" @click.stop="copy(req.full_result)" title="Copy full result">📋</button>
              </summary>
              <pre class="scroll">{{ req.full_result }}</pre>
            </details>

            <button class="btn btn--ghost small" @click="viewScreenshot(req.request_id)">View Screenshot</button>
       <button
  class="btn btn--ghost small"
  :disabled="isRequeuing(req.request_id) || normalizeStatus(req.status)==='queued'"
  @click="confirmRequeue(req)"
>
  {{ isRequeuing(req.request_id) ? 'Requeuing…' : 'Requeue' }}
</button>

          </div>
        </article>
      </div>

      <!-- Pagination -->
      <div class="pagination">
        <button class="btn btn--ghost" @click="prevPage" :disabled="page === 1">Prev</button>
        <span>Page {{ page }} of {{ totalPages }}</span>
        <button class="btn btn--ghost" @click="nextPage" :disabled="page >= totalPages">Next</button>
      </div>
    </template>

    <!-- Empty state -->
    <p v-else class="empty">No requests found.</p>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'RequestMonitor',
  data() {
    return {
      requeueWorking: new Set(), // request_ids currently requeueing
      requests: [],
      statusMessage: '',
      statusType: '',
      page: 1,
      limit: 20,
      total: 0,
      isLoading: false,
      // UI helpers
      search: '',
      statusFilter: 'all',
      lastQueryStamp: 0,
    };
  },
  mounted() {
    this.fetchRequests();
  },
  computed: {
    // Now the backend is responsible for search + status filtering.
    // This computed simply exposes whatever the server returned.
    filteredRequests() {
      return this.requests;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    },
    pagedRequests() {
      // We are already paging on the server, so just return filteredRequests.
      return this.filteredRequests;
    },
  },
  watch: {
    // Whenever the status filter changes, re-query the backend starting from page 1
    statusFilter() {
      this.page = 1;
      this.fetchRequests();
    },
  },
  methods: {
  formatHopLogic(v) {
  const x = String(v || 'fifo').toLowerCase().trim();
  if (x === 'lifo') return 'LIFO';
  if (x === 'next_out') return 'Next Out';
  return 'FIFO';
},

    isRequeuing(id) {
      return this.requeueWorking.has(id);
    },

    async confirmRequeue(req) {
      const msg =
        `Requeue request #${req.request_id} (${req.username || 'user'})?\n\n` +
        `Tx: ${req.transaction_hash || '—'}\nCurrent status: ${req.status}`;
      if (!window.confirm(msg)) return;
      await this.requeueRequest(req);
    },

    async requeueRequest(req) {
      const id = req.request_id;
      try {
        this.requeueWorking.add(id);
        // optimistic UI -> mark as queued immediately
        const prev = req.status;
        req.status = 'queued';

        const res = await axios.post(
          'etherscan/requeue_request.php',
          { request_id: id },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );

        if (res.data?.status !== 'success') {
          // revert optimistic change on failure
          req.status = prev;
          throw new Error(res.data?.message || 'Requeue failed.');
        }

        this.statusMessage = `Request #${id} requeued.`;
        this.statusType = 'success';

        // Optional: if you want latest server truth, uncomment:
        // await this.fetchRequests();
      } catch (e) {
        console.error(e);
        this.statusMessage = e.message || 'Failed to requeue request.';
        this.statusType = 'error';
      } finally {
        this.requeueWorking.delete(id);
        // auto-clear success banner after a moment
        setTimeout(() => {
          if (this.statusMessage && this.statusType === 'success') {
            this.statusMessage = '';
          }
        }, 2500);
      }
    },

    async refresh() {
      // Refresh keeps current search / status, but resets to first page
      this.page = 1;
      await this.fetchRequests();
    },

    applySearch() {
      // Move to first page of results for this search term and re-query the server
      this.page = 1;
      this.fetchRequests();
    },

    viewScreenshot(requestID) {
      const route = this.$router.resolve({
        name: 'AdminViewImage',
        params: { requestID: String(requestID) },
      });

      window.open(route.href, '_blank', 'noopener,noreferrer');
    },

    copy(text) {
      navigator.clipboard?.writeText(text).catch(() => {});
    },

    rowClass(req) {
      const stale = this.isStale(req.request_time);
      if (req.status === 'error') return 'row row--error';
      if (stale && req.status === 'queued') return 'row row--stale-queued';
      if (stale && req.status === 'in progress') return 'row row--stale-inprogress';
      return 'row';
    },

    normalizeStatus(s) {
      return String(s || '').toLowerCase().trim();
    },

    isQueueLike(req) {
      const st = this.normalizeStatus(req.status);
      return st === 'queued' || st === 'pending';
    },

    pillClass(req) {
      switch (this.normalizeStatus(req.status)) {
        case 'complete': return 'pill--ok';
        case 'error': return 'pill--error';
        case 'queued': return 'pill--queued';
        case 'pending': return 'pill--progress';
        default: return '';
      }
    },

    async fetchRequests() {
      try {
        this.isLoading = true;
        const stamp = Date.now();
        this.lastQueryStamp = stamp;

        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Not authenticated');
        }

        // Build query params for backend search + filter
        const params = {
          limit: this.limit,
          page: this.page,
        };

        const trimmedSearch = this.search.trim();
        if (trimmedSearch) {
          params.search = trimmedSearch;
        }

        // Only send status filter if not "all"
        if (this.statusFilter && this.statusFilter !== 'all') {
          params.status = this.statusFilter;
        }

        const res = await axios.get('etherscan/fetch_all_requests.php', {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Ignore out-of-order responses
        if (stamp !== this.lastQueryStamp) return;

        const data = res.data || {};
        if (data.status === 'success') {
          this.requests = data.requests || [];
          // total is the count of rows that match the current search + status filter
          this.total = data.total || this.requests.length;
          this.statusMessage = '';
          this.statusType = '';
        } else {
          this.requests = [];
          this.total = 0;
          this.statusMessage = data.message || 'Unexpected response.';
          this.statusType = 'error';
        }
      } catch (err) {
        console.error('RequestMonitor error:', err);
        this.requests = [];
        this.total = 0;
        this.statusMessage = err.message || 'Failed to fetch requests.';
        this.statusType = 'error';
      } finally {
        this.isLoading = false;
      }
    },

    isStale(requestTime) {
      const t = new Date(requestTime);
      const tenMinAgo = Date.now() - 10 * 60 * 1000;
      return t.getTime() < tenMinAgo;
    },

    formatTime(iso) {
      return new Date(iso).toLocaleString();
    },

    async nextPage() {
      if (this.page < this.totalPages) {
        this.page++;
        await this.fetchRequests();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    async prevPage() {
      if (this.page > 1) {
        this.page--;
        await this.fetchRequests();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
  },
};
</script>

<style scoped>
/* Layout */
.request-monitor {
  max-width: 1120px;
  margin: 0 auto;
  padding: 1rem;
  color: #1f2a37;
}
.rm-header {
  display: grid;
  gap: .75rem;
  grid-template-columns: 1fr;
  margin-bottom: .75rem;
}
.rm-header h1 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 800;
  color: #0f2740;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
  align-items: center;
}
.input-group {
  display: flex;
  gap: .4rem;
  flex: 1 1 420px;
}
.input {
  flex: 1;
  min-width: 220px;
  padding: .6rem .7rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
}
.select {
  padding: .6rem .7rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  min-width: 160px;
}

/* Buttons */
.btn {
  padding: .6rem .8rem;
  border-radius: 10px;
  border: 1px solid rgba(15,38,61,.15);
  background: linear-gradient(180deg,#ffffff,#f3f4f6);
  cursor: pointer;
  font-weight: 600;
}
.btn:hover { background: linear-gradient(180deg,#f9fafb,#eef2f7); }
.btn:disabled { opacity: .6; cursor: not-allowed; }
.btn--ghost {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}
.small { padding: .4rem .6rem; font-size: .9rem; }

.icon-btn {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  padding: .1rem .35rem;
  margin-left: .35rem;
  cursor: pointer;
}
.icon-btn:hover { background: #f3f4f6; }

/* Status message */
.status {
  margin: .4rem 0 .2rem;
  font-weight: 600;
}
.status.success { color: #0f766e; }
.status.error { color: #b91c1c; }

/* Table */
.table-wrap {
  overflow-x: auto;         /* prevents pushing page to the right */
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 20px rgba(16,24,40,.06);
}
.rm-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 980px;         /* keep columns readable on larger screens */
  table-layout: fixed;
}
.rm-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f3f6fb;
  color: #0f2740;
  text-align: left;
  font-weight: 700;
  font-size: .92rem;
  border-bottom: 1px solid #e5e7eb;
  padding: .7rem .7rem;
}
.rm-table td {
  padding: .6rem .7rem;
  border-bottom: 1px solid #eef1f5;
  vertical-align: top;
  word-wrap: break-word;
}

/* Cells */
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }
.semi { font-weight: 500; }
.truncate {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.result-cell details summary {
  cursor: pointer;
  color: #0b5ed7;
}
pre {
  margin: .35rem 0 0;
  font-size: .88rem;
  line-height: 1.4;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: .6rem .7rem;
}
pre.scroll { max-height: 300px; overflow-y: auto; }

/* Row states */
.row--error { background: #fff5f5; }
.row--stale-queued { background: #fffaf0; }
.row--stale-inprogress { background: #fff7ea; }

/* Pills */
.pill {
  display: inline-block;
  padding: .15rem .5rem;
  border-radius: 999px;
  font-size: .78rem;
  font-weight: 700;
  border: 1px solid transparent;
}
.pill--ok { background: #e8faf3; color: #065f46; border-color: #a7f3d0; }
.pill--error { background: #fde8e8; color: #991b1b; border-color: #fecaca; }
.pill--queued { background: #eef2ff; color: #4338ca; border-color: #c7d2fe; }
.pill--progress { background: #ecfeff; color: #155e75; border-color: #a5f3fc; }
.pill--warn { background: #fff7ed; color: #9a3412; border-color: #fed7aa; }

/* Empty */
.empty { text-align: center; color: #6b7280; margin: 1rem 0; }

/* Pagination */
.pagination {
  display: flex; gap: .6rem; align-items: center; justify-content: center;
  margin: 1rem 0 .2rem;
}

/* Mobile Cards */
@media (max-width: 900px) {
  .table-wrap { display: none; }
  .cards { display: grid; gap: .8rem; }
  .card {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 6px 14px rgba(16,24,40,.06);
    padding: .8rem .9rem;
  }
  .card__header {
    display: flex; align-items: center; justify-content: space-between; gap: .6rem;
    margin-bottom: .35rem;
  }
  .card__title { font-weight: 800; color: #0f2740; }
  .card__tags { display: flex; gap: .35rem; flex-wrap: wrap; }
  .kv { display: grid; gap: .3rem; }
  .kv > div { display: grid; grid-template-columns: 100px 1fr; gap: .4rem; align-items: baseline; }
  .kv dt { color: #6b7280; font-size: .86rem; }
  .kv dd { margin: 0; }
  .card__actions { margin-top: .5rem; display: grid; gap: .4rem; }
}

.cards { display: none; }
.col-hop {
  width: 90px;
  white-space: nowrap;
}



</style>
